@file:OptIn(ExperimentalMaterial3Api::class)

package us.laantech.common.ui

import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color


// TODO when the list is empty, show loading indicator or message

/**
 * A reusable single-select dropdown built on top of [ExposedDropdownMenuBox].
 *
 * Example Usage:
 * ```
 *     DropdownSelectView(
 *         label = "Employee",
 *         items = uiState.employeeList,
 *         selectedItem = uiState.employeeList.firstOrNull { it.uuid == uiState.selectedEmployeeUUID },
 *         itemLabel = { it.employeeName },
 *         noneLabel = "Any Employee",
 *         onItemSelected = { employee -> viewModel.selectEmployee(employee?.uuid) },
 *     )
 * ```
 *
 * @param label          The label shown above the text field.
 * @param items          The list of items the user can pick from.
 * @param selectedItem   The currently-selected item, or `null` for the "none" entry.
 * @param itemLabel      How to render each item as text.
 * @param onItemSelected Called when the user picks an item. `null` means the "none" option was chosen.
 * @param modifier       Modifier applied to the [OutlinedTextField].
 * @param noneLabel      Text shown for the "no selection" option. If `null`, the "none"
 *                       entry is hidden and the selection is required.
 */
@Composable
fun <T> DropdownMenu(
    label: String,
    items: List<T>,
    selectedItem: T?,
    itemLabel: (T) -> String,
    onItemSelected: (T?) -> Unit,
    modifier: Modifier = Modifier,
    noneLabel: String? = null,
) {
    var expanded by remember { mutableStateOf(false) }

    val displayedText = selectedItem?.let(itemLabel) ?: noneLabel ?: ""

    ExposedDropdownMenuBox(
        expanded = expanded,
        onExpandedChange = { expanded = it },
        modifier = modifier,
    ) {
        OutlinedTextField(
            value = displayedText,
            onValueChange = {},
            readOnly = true,
            label = { Text(label) },
            trailingIcon = { ExposedDropdownMenuDefaults.TrailingIcon(expanded = expanded) },
            colors = ExposedDropdownMenuDefaults.outlinedTextFieldColors(),
            modifier = Modifier
                .menuAnchor(MenuAnchorType.PrimaryNotEditable)
                .fillMaxWidth(),
        )
        ExposedDropdownMenu(
            expanded = expanded,
            onDismissRequest = { expanded = false },
            containerColor = Color.White
        ) {
            if (noneLabel != null) {
                DropdownMenuItem(
                    text = { Text(noneLabel, color = Color.Gray) },
                    onClick = {
                        onItemSelected(null)
                        expanded = false
                    },
                )
            }
            items.forEach { item ->
                DropdownMenuItem(
                    text = { Text(itemLabel(item), color = Color.Gray) },
                    onClick = {
                        onItemSelected(item)
                        expanded = false
                    },
                )
            }
        }
    }
}